import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiDelete } from '../../utils/api';
import { toast } from 'sonner';
import {
  Target, Users, DollarSign, Zap, Shield,
  AlertTriangle, Lightbulb, CheckCircle2, Play, ChevronRight,
  MoreVertical, Trash2, Pencil, Copy,
} from 'lucide-react';

export const THEME_ICONS = { Goal: Target, Customer: Users, "Revenue Model": DollarSign, Execution: Zap, Risk: Shield, People: Users };
export const THEME_COLORS = { Goal: 'text-blue-500 bg-blue-500/10', Customer: 'text-emerald-500 bg-emerald-500/10', "Revenue Model": 'text-amber-500 bg-amber-500/10', Execution: 'text-violet-500 bg-violet-500/10', Risk: 'text-red-500 bg-red-500/10', People: 'text-teal-500 bg-teal-500/10' };

export function ScoreBar({ score, max = 10, color = 'bg-odapto-orange' }) {
  const pct = Math.round((score / max) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold w-5 text-right">{score}</span>
    </div>
  );
}

export function OverallScore({ score }) {
  const gc = (s) => s >= 80 ? 'text-emerald-500 border-emerald-500/30 bg-emerald-500/5' : s >= 60 ? 'text-amber-500 border-amber-500/30 bg-amber-500/5' : 'text-red-500 border-red-500/30 bg-red-500/5';
  return (<div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border ${gc(score)}`}><span className="text-lg font-bold">{score}</span><span className="text-[10px] opacity-70">/100</span></div>);
}

export function PastSessions() {
  const [sessions, setSessions] = useState([]);
  const [menuOpen, setMenuOpen] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const fetchSessions = () => {
    apiGet('/strategy-coach/sessions').then(r => r.ok ? r.json() : []).then(setSessions).catch(() => {});
  };
  useEffect(() => { fetchSessions(); }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await apiDelete(`/strategy-coach/sessions/${deleteTarget.session_id}`);
      if (res.ok) {
        setSessions(prev => prev.filter(s => s.session_id !== deleteTarget.session_id));
        toast.success('Session deleted');
      } else {
        toast.error('Failed to delete session');
      }
    } catch {
      toast.error('Connection error');
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  if (!sessions.length) return null;
  return (
    <div className="mt-10 w-full max-w-2xl mx-auto">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Previous Sessions</h3>
      <div className="space-y-2">
        {sessions.slice(0, 10).map(s => (
          <div key={s.session_id} className="relative flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors" data-testid={`session-${s.session_id}`}>
            <button onClick={() => navigate(`/strategy-coach/${s.session_id}`)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${s.status === 'completed' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}`}>
                {s.status === 'completed' ? <CheckCircle2 className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{s.custom_question || s.journey_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
                <p className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${s.status === 'completed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                {s.status === 'completed' ? 'Completed' : 'In Progress'}
              </span>
            </button>

            {/* 3-dot menu */}
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === s.session_id ? null : s.session_id); }}
                className="p-1.5 rounded-md hover:bg-muted transition-colors"
                data-testid={`session-menu-${s.session_id}`}
              >
                <MoreVertical className="w-4 h-4 text-muted-foreground" />
              </button>
              {menuOpen === s.session_id && (
                <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-border bg-card shadow-lg z-20 py-1" data-testid={`session-dropdown-${s.session_id}`}>
                  <button onClick={() => { navigate(`/strategy-coach/${s.session_id}`); setMenuOpen(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50 transition-colors text-left">
                    <Play className="w-3.5 h-3.5" /> Resume Session
                  </button>
                  <button disabled className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground/50 cursor-not-allowed text-left">
                    <Pencil className="w-3.5 h-3.5" /> Rename Session <span className="ml-auto text-[9px] bg-muted px-1 rounded">Soon</span>
                  </button>
                  <button disabled className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground/50 cursor-not-allowed text-left">
                    <Copy className="w-3.5 h-3.5" /> Duplicate Session <span className="ml-auto text-[9px] bg-muted px-1 rounded">Soon</span>
                  </button>
                  <div className="border-t border-border my-1" />
                  <button
                    onClick={() => { setDeleteTarget(s); setMenuOpen(null); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-500/5 transition-colors text-left"
                    data-testid={`session-delete-${s.session_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete Session
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="delete-confirm-dialog">
          <div className="bg-card border border-border rounded-2xl p-6 max-w-md mx-4 shadow-xl">
            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <h2 className="text-lg font-bold text-center mb-2">Delete Session?</h2>
            <p className="text-sm text-muted-foreground text-center mb-4">
              Deleting this session will permanently remove:
            </p>
            <ul className="text-xs text-muted-foreground space-y-1 mb-5 bg-red-500/5 border border-red-500/10 rounded-lg p-3">
              <li className="flex items-center gap-1.5"><span className="text-red-400">•</span> Conversation history</li>
              <li className="flex items-center gap-1.5"><span className="text-red-400">•</span> AI questions and answers</li>
              <li className="flex items-center gap-1.5"><span className="text-red-400">•</span> Strategic diagnosis</li>
              <li className="flex items-center gap-1.5"><span className="text-red-400">•</span> Generated insights</li>
            </ul>
            <p className="text-xs text-red-500 font-medium text-center mb-5">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
                data-testid="delete-confirm-btn"
              >
                {deleting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DetectionBanner({ detection }) {
  if (!detection) return null;
  const items = [];
  if (detection.assumption) items.push({ type: 'Assumption', flag: detection.assumption.flag, detail: detection.assumption.statement, icon: AlertTriangle, color: 'border-amber-500/30 bg-amber-50 dark:bg-amber-900/10 text-amber-800 dark:text-amber-200' });
  if (detection.contradiction) items.push({ type: 'Contradiction', flag: detection.contradiction.flag, detail: `Earlier: "${detection.contradiction.earlier}" vs Now: "${detection.contradiction.now}"`, icon: Shield, color: 'border-red-500/30 bg-red-50 dark:bg-red-900/10 text-red-800 dark:text-red-200' });
  if (detection.opportunity) items.push({ type: 'Opportunity', flag: detection.opportunity.flag, detail: detection.opportunity.signal, icon: Lightbulb, color: 'border-emerald-500/30 bg-emerald-50 dark:bg-emerald-900/10 text-emerald-800 dark:text-emerald-200' });
  if (!items.length) return null;
  return (
    <div className="space-y-2 mx-auto max-w-md">
      {items.map((item, i) => { const Icon = item.icon; return (
        <div key={i} className={`rounded-lg border p-3 ${item.color}`}>
          <div className="flex items-center gap-2 mb-1"><Icon className="w-3.5 h-3.5" /><span className="text-xs font-semibold uppercase tracking-wider">{item.flag || item.type + ' Detected'}</span></div>
          <p className="text-xs opacity-80">{item.detail}</p>
        </div>
      ); })}
    </div>
  );
}
